import React, { useState, useEffect } from 'react';
import { Plus, Search, SlidersHorizontal, Download, X } from 'lucide-react';
import { useContracts } from '../../../hooks/useContracts';
import { useEmployees } from '../../../hooks/useEmployees';
import ContractForm from './ContractForm';
import ContractDetails from './ContractDetails';
import ContractsList from './ContractsList';

const ContractsModule = () => {
  const { contracts, loading, error, loadContracts, loadStats, deleteContract } = useContracts();
  const { employees, loadEmployees } = useEmployees();

  const [view, setView]                     = useState('list'); // 'list' | 'form' | 'details'
  const [selectedContract, setSelectedContract] = useState(null);
  const [searchTerm, setSearchTerm]         = useState('');
  const [filterEstado, setFilterEstado]     = useState('all');
  const [filterTipo, setFilterTipo]         = useState('all');
  const [stats, setStats]                   = useState(null);
  const [showFilters, setShowFilters]       = useState(false);

  useEffect(() => { loadData(); }, []); // eslint-disable-line

  const loadData = async () => {
    await Promise.all([loadContracts(), loadEmployees(), loadStatsData()]);
  };

  const loadStatsData = async () => {
    const result = await loadStats();
    if (result.success) setStats(result.stats);
  };

  const handleCreate = () => { setSelectedContract(null); setView('form'); };

  const handleEdit = async (contract) => {
    try {
      const { contractsAPI } = await import('../../../api/contracts');
      const result = await contractsAPI.getById(contract.id);
      if (result.success) { setSelectedContract(result.contract); setView('form'); }
      else alert('Error al cargar el contrato: ' + result.error);
    } catch { alert('Error al cargar el contrato'); }
  };

  const handleView = (contract) => { setSelectedContract(contract); setView('details'); };

  const handleDelete = async (contract) => {
    const motivo = prompt('¿Motivo de rescisión del contrato?');
    if (!motivo) return;
    if (window.confirm(`¿Estás seguro de rescindir el contrato de ${contract.empleado_nombre}?`)) {
      const result = await deleteContract(contract.id, motivo);
      if (result.success) { alert('Contrato rescindido correctamente'); loadData(); }
      else alert(result.error);
    }
  };

  const handleFormClose = (shouldReload) => {
    setView('list'); setSelectedContract(null);
    if (shouldReload) loadData();
  };

  const handleDetailsClose = () => { setView('list'); setSelectedContract(null); };

  const filteredContracts = contracts.filter(c => {
    const matchesSearch =
      c.empleado_nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.empleado_codigo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.numero_contrato?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.cargo?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesEstado = filterEstado === 'all' || c.estado === filterEstado;
    const matchesTipo   = filterTipo   === 'all' || c.tipo_contrato === filterTipo;
    return matchesSearch && matchesEstado && matchesTipo;
  });

  const filtersActive = filterEstado !== 'all' || filterTipo !== 'all';

  // ── Form view ──────────────────────────────────────────────────
  if (view === 'form') {
    return <ContractForm contract={selectedContract} employees={employees} onClose={handleFormClose} />;
  }

  // ── Details view ───────────────────────────────────────────────
  if (view === 'details' && selectedContract) {
    return (
      <ContractDetails
        contract={selectedContract}
        onClose={handleDetailsClose}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />
    );
  }

  // ── List view ──────────────────────────────────────────────────
  return (
    <div className="space-y-4">

      {/* ── Toolbar ──────────────────────────────────────────────── */}
      <div className="flex items-center justify-between">
        <span className="text-sm text-gray-400 bg-gray-100 px-2.5 py-1 rounded-full font-medium">
          {filteredContracts.length} de {contracts.length}
        </span>

        <div className="flex items-center gap-2">
          <button
            onClick={handleCreate}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus size={15} />
            Nuevo contrato
          </button>
        </div>
      </div>

      {/* ── Search + Filter bar ──────────────────────────────────── */}
      <div className="flex items-center gap-3">
        {/* Search */}
        <div className="relative flex-1 max-w-sm">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar contrato..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400 bg-white"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-300 hover:text-gray-500"
            >
              <X size={14} />
            </button>
          )}
        </div>

        {/* Filters toggle */}
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`flex items-center gap-2 px-3 py-2 text-sm border rounded-lg transition-colors ${
            showFilters || filtersActive
              ? 'border-blue-400 text-blue-600 bg-blue-50'
              : 'border-gray-200 text-gray-500 hover:bg-gray-50'
          }`}
        >
          <SlidersHorizontal size={15} />
          Filtros
          {filtersActive && <span className="w-2 h-2 rounded-full bg-blue-500" />}
        </button>

        {/* Stats inline */}
        {stats && (
          <div className="hidden sm:flex items-center gap-2 ml-auto">
            <span className="text-xs text-gray-400">
              <span className="font-semibold text-green-600">{stats.activos || 0}</span> activos
            </span>
            <span className="text-gray-200">|</span>
            <span className="text-xs text-gray-400">
              <span className="font-semibold text-amber-500">{stats.por_vencer || 0}</span> por vencer
            </span>
            <span className="text-gray-200">|</span>
            <span className="text-xs text-gray-400">
              <span className="font-semibold text-gray-500">{stats.finalizados || 0}</span> finalizados
            </span>
          </div>
        )}
      </div>

      {/* ── Filter dropdowns (expandible) ───────────────────────── */}
      {showFilters && (
        <div className="flex items-center gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
          <select
            value={filterEstado}
            onChange={(e) => setFilterEstado(e.target.value)}
            className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:border-blue-400"
          >
            <option value="all">Todos los estados</option>
            <option value="activo">Activos</option>
            <option value="finalizado">Finalizados</option>
            <option value="rescindido">Rescindidos</option>
            <option value="suspendido">Suspendidos</option>
          </select>
          <select
            value={filterTipo}
            onChange={(e) => setFilterTipo(e.target.value)}
            className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:border-blue-400"
          >
            <option value="all">Todos los tipos</option>
            <option value="indefinido">Indefinido</option>
            <option value="plazo_fijo">Plazo Fijo</option>
            <option value="temporal">Temporal</option>
            <option value="por_obra">Por Obra</option>
            <option value="prueba">Período de Prueba</option>
          </select>
          {filtersActive && (
            <button
              onClick={() => { setFilterEstado('all'); setFilterTipo('all'); }}
              className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1"
            >
              <X size={12} /> Limpiar filtros
            </button>
          )}
        </div>
      )}

      {/* ── Table ───────────────────────────────────────────────── */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto mb-3" />
            <p className="text-sm text-gray-400">Cargando contratos...</p>
          </div>
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
          <p className="text-red-600 font-semibold text-sm">{error}</p>
        </div>
      ) : (
        <ContractsList
          contracts={filteredContracts}
          onView={handleView}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      )}
    </div>
  );
};

export default ContractsModule;
